import { algoliaClient, FREELANCERS_INDEX, JOBS_INDEX } from '../../../lib/algolia';
import { FreelancerAlgoliaRecord, JobAlgoliaRecord } from '../../../types/algolia';
import { FreelancerSearchResult } from '../../../types/search';
import { JobSearchResult } from '../../../types/job';
import { SearchStrategy, FreelancerSearchParams, JobSearchParams } from '../SearchStrategy';
import type { SearchResponse } from 'algoliasearch';

export class AlgoliaFreelancerStrategy implements SearchStrategy<
  FreelancerSearchResult,
  FreelancerSearchParams
> {
  private normalizeNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  async search(
    params: FreelancerSearchParams,
    currentUserId?: string
  ): Promise<{ results: FreelancerSearchResult[]; hasMore: boolean }> {
    const algoliaFilters: string[] = [];
    if (params.filter_languages && params.filter_languages.length > 0) {
      algoliaFilters.push(
        `(${params.filter_languages.map(lang => `languages:${lang}`).join(' OR ')})`
      );
    }
    if (params.filter_service_category_ids && params.filter_service_category_ids.length > 0) {
      algoliaFilters.push(
        `(${params.filter_service_category_ids.map(id => `service_category_ids:${id}`).join(' OR ')})`
      );
    }
    if (params.filter_city_ids && params.filter_city_ids.length > 0) {
      algoliaFilters.push(`(${params.filter_city_ids.map(id => `city_id:${id}`).join(' OR ')})`);
    }

    const response = await algoliaClient.search({
      requests: [
        {
          indexName: FREELANCERS_INDEX,
          query: params.keyword,
          hitsPerPage: params.limit_count || 20,
          page: Math.floor((params.offset_count || 0) / (params.limit_count || 20)),
          filters: algoliaFilters.length > 0 ? algoliaFilters.join(' AND ') : undefined,
          aroundLatLng:
            params.origin_lat && params.origin_lon
              ? `${params.origin_lat},${params.origin_lon}`
              : undefined,
        },
      ],
    });

    const result = response.results[0] as SearchResponse;
    const hits = (result.hits as unknown as FreelancerAlgoliaRecord[]) || [];
    const hasMore = (result.nbPages || 0) > (result.page || 0) + 1;

    // Mapping to FreelancerSearchResult
    const rawResults: FreelancerSearchResult[] = hits.map(h => ({
      id: h.objectID,
      first_name: h.first_name,
      last_name: null, // Privacy-safe default
      job_title: h.job_title,
      service_category_slugs: h.service_category_slugs || [],
      avatar_url: h.avatar_url,
      bio: h.bio,
      city: h.city,
      alliance_count: h.alliance_count || 0,
      recent_alliances: [],
      full_access: false,
      alliance_status: 'pending',
      overall_rating: h.overall_rating || 0,
      reviews_count: h.reviews_count || 0,
      projects_count: h.projects_count || 0,
      distance: h._rankingInfo?.geoDistance || null,
    }));

    if (!currentUserId || rawResults.length === 0) {
      return { results: rawResults, hasMore };
    }

    try {
      const profileIds = rawResults.map(result => result.id);
      const { supabase } = await import('../../../lib/supabase');

      const [
        { data: sentAlliances, error: sentAlliancesError },
        { data: receivedAlliances, error: receivedAlliancesError },
        { data: realProfiles, error: realProfilesError },
      ] = await Promise.all([
        supabase
          .from('alliances')
          .select('user_id_2')
          .eq('user_id_1', currentUserId)
          .eq('status', 'accepted')
          .in('user_id_2', profileIds),
        supabase
          .from('alliances')
          .select('user_id_1')
          .eq('user_id_2', currentUserId)
          .eq('status', 'accepted')
          .in('user_id_1', profileIds),
        supabase.from('profiles').select('id, last_name').in('id', profileIds),
      ]);

      if (sentAlliancesError || receivedAlliancesError || realProfilesError) {
        throw sentAlliancesError || receivedAlliancesError || realProfilesError;
      }

      const allianceMap = new Set<string>([
        ...((sentAlliances ?? []) as Array<{ user_id_2: string }>).map(
          alliance => alliance.user_id_2
        ),
        ...((receivedAlliances ?? []) as Array<{ user_id_1: string }>).map(
          alliance => alliance.user_id_1
        ),
      ]);
      const normalizedProfiles: Array<{ id: string; last_name: string | null }> = (
        realProfiles ?? []
      ).map((profile: { id: unknown; last_name: unknown }) => ({
        id: String((profile as { id: unknown }).id),
        last_name: this.normalizeNullableString((profile as { last_name: unknown }).last_name),
      }));
      const profileMap = new Map(
        normalizedProfiles.map(profile => [profile.id, profile.last_name])
      );

      const results: FreelancerSearchResult[] = rawResults.map(result => {
        const isAllied = allianceMap.has(result.id) || result.id === currentUserId;

        return {
          ...result,
          last_name: isAllied ? (profileMap.get(result.id) ?? result.last_name) : null,
          full_access: isAllied,
          alliance_status: (isAllied ? 'accepted' : 'pending') as 'accepted' | 'pending' | 'none',
        };
      });

      return { results, hasMore };
    } catch (error) {
      console.error('Failed to enrich Algolia freelancer results with alliance data:', error);
      return { results: rawResults, hasMore };
    }
  }

  async fetchSuggestions(keyword: string) {
    const response = await algoliaClient.search({
      requests: [
        {
          indexName: FREELANCERS_INDEX,
          query: keyword,
          hitsPerPage: 20,
        },
      ],
    });
    const result = response.results[0] as SearchResponse;
    const hits = (result.hits as unknown as FreelancerAlgoliaRecord[]) || [];
    const suggestions = new Set<string>();
    hits.forEach(h => {
      h.service_category_labels?.forEach(l => suggestions.add(l));
    });
    return Array.from(suggestions).slice(0, 10);
  }
}

export class AlgoliaJobStrategy implements SearchStrategy<JobSearchResult, JobSearchParams> {
  async search(params: JobSearchParams) {
    const algoliaFilters: string[] = [];
    if (params.filter_service_category_slugs && params.filter_service_category_slugs.length > 0) {
      algoliaFilters.push(
        `(${params.filter_service_category_slugs.map(slug => `service_category_slugs:${slug}`).join(' OR ')})`
      );
    }
    if (params.filter_city_ids && params.filter_city_ids.length > 0) {
      algoliaFilters.push(`(${params.filter_city_ids.map(id => `city_id:${id}`).join(' OR ')})`);
    }
    if (params.filter_languages && params.filter_languages.length > 0) {
      algoliaFilters.push(
        `(${params.filter_languages.map(lang => `languages:${lang}`).join(' OR ')})`
      );
    }

    const response = await algoliaClient.search({
      requests: [
        {
          indexName: JOBS_INDEX,
          query: params.keyword,
          hitsPerPage: params.limit_count || 20,
          page: Math.floor((params.offset_count || 0) / (params.limit_count || 20)),
          filters: algoliaFilters.length > 0 ? algoliaFilters.join(' AND ') : undefined,
        },
      ],
    });

    const result = response.results[0] as SearchResponse;
    const hits = (result.hits as unknown as JobAlgoliaRecord[]) || [];
    const hasMore = (result.nbPages || 0) > (result.page || 0) + 1;

    const results: JobSearchResult[] = hits.map(h => ({
      id: h.objectID,
      title: h.title,
      description: h.description,
      published_at: h.published_at,
      created_at: h.created_at,
      status: h.status,
      visibility: 'public',
      start_date: null,
      end_date: null,
      expires_at: null,
      listing_type_id: h.listing_type_credits,
      listing_type_code: h.listing_type,
      service_category_slugs: h.service_category_slugs,
      industry_slugs: [],
      languages: h.languages,
      city_id: h.city_id,
      city_name: h.city || '',
      on_site: false,
      budget_range_id: 0,
      budget_range_code: h.budget_code || '',
      budget_range_min_amount: h.budget_min,
      budget_range_max_amount: h.budget_max,
      client_id: h.client.first_name, // Placeholder or fetch if needed
      client_first_name: h.client.first_name,
      client_last_name: h.client.last_name,
      client_company_name: h.client.company_name,
      client_avatar_url: h.client.avatar_url,
      shadow_recruiter_id: h.shadow_recruiter_id ?? null,
      shadow_recruiter_company_name: h.shadow_recruiter_company_name ?? null,
      is_shadow_recruiter: h.is_shadow_recruiter ?? false,
      recruiter_display_name: h.recruiter_display_name ?? null,
      recruiter_display_context: h.recruiter_display_context ?? null,
      updated_at: h.published_at,
      has_user_quote: false,
    }));

    return { results, hasMore };
  }

  async fetchSuggestions(keyword: string) {
    const response = await algoliaClient.search({
      requests: [
        {
          indexName: JOBS_INDEX,
          query: keyword,
          hitsPerPage: 20,
        },
      ],
    });
    const result = response.results[0] as SearchResponse;
    const hits = (result.hits as unknown as JobAlgoliaRecord[]) || [];
    const suggestions = new Set<string>();
    hits.forEach(h => {
      h.service_category_labels?.forEach(l => suggestions.add(l));
    });
    return Array.from(suggestions).slice(0, 10);
  }
}
